import { createSlice } from '@reduxjs/toolkit'
import { removeBoard } from '../shared/boardCleanup.actions'
import {
  createEmptyBoardStickersTabBoardState,
  createInitialBoardStickersTabState,
} from './boardStickersTab.state.utils'
import {
  fetchBoardStickersTabThunk,
  getFetchBoardStickersTabBoardId,
  reorderBoardStickerThunk,
  setBoardStickerRequiredThunk,
  setBoardStickerVisibilityThunk,
  type FetchBoardStickersTabArg,
} from './boardStickersTab.thunks'
import type { TaskTrackerBoardSticker } from '../domain.types'
import {
  hasInvalidBoardStickerPositions,
  preferOptimisticBoardStickerPositions,
} from '../../lib/taskTrackerBoardStickerRows.utils'

const shouldSilentFetchBoardStickersTab = (arg: FetchBoardStickersTabArg) =>
  typeof arg === 'object' && Boolean(arg.silent)

const ensureBoard = (
  state: ReturnType<typeof createInitialBoardStickersTabState>,
  boardId: number,
) => {
  if (!state.byBoardId[boardId]) {
    state.byBoardId[boardId] = createEmptyBoardStickersTabBoardState()
  }
  return state.byBoardId[boardId]
}

const hasLoadedBoardStickersTabData = (
  tabState: ReturnType<typeof createEmptyBoardStickersTabBoardState>,
) => tabState.boardStickers.length > 0 || tabState.catalog.length > 0

const boardStickersTabSlice = createSlice({
  name: 'taskTrackerBoardStickersTab',
  initialState: createInitialBoardStickersTabState(),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(removeBoard, (state, action) => {
        delete state.byBoardId[action.payload.boardId]
      })
      .addCase(fetchBoardStickersTabThunk.pending, (state, action) => {
        if (shouldSilentFetchBoardStickersTab(action.meta.arg)) return

        const tabState = ensureBoard(
          state,
          getFetchBoardStickersTabBoardId(action.meta.arg),
        )
        tabState.error = null

        if (!hasLoadedBoardStickersTabData(tabState)) {
          tabState.loading = true
        }
      })
      .addCase(fetchBoardStickersTabThunk.fulfilled, (state, action) => {
        const tabState = ensureBoard(state, action.payload.boardId)
        tabState.boardStickers = action.payload.stickers
        tabState.catalog = []
        tabState.error = null
        if (!action.payload.silent) {
          tabState.loading = false
        }
      })
      .addCase(fetchBoardStickersTabThunk.rejected, (state, action) => {
        const payload = action.payload as {
          boardId: number
          silent?: boolean
          error: string
        }
        if (payload.silent) return

        const tabState = ensureBoard(state, payload.boardId)
        tabState.loading = false
        tabState.catalog = []
        tabState.boardStickers = []
        tabState.error = payload.error
      })
      .addCase(setBoardStickerVisibilityThunk.pending, (state, action) => {
        const tabState = ensureBoard(state, action.meta.arg.boardId)
        const fieldSettingId = action.meta.arg.fieldSettingId
        if (!tabState.visibilityLoadingIds.includes(fieldSettingId)) {
          tabState.visibilityLoadingIds.push(fieldSettingId)
        }
      })
      .addCase(setBoardStickerVisibilityThunk.fulfilled, (state, action) => {
        const tabState = ensureBoard(state, action.payload.boardId)
        tabState.boardStickers = action.payload.stickers
        const fieldSettingId = action.meta.arg.fieldSettingId
        tabState.visibilityLoadingIds = tabState.visibilityLoadingIds.filter(
          (itemId) => itemId !== fieldSettingId,
        )
      })
      .addCase(setBoardStickerVisibilityThunk.rejected, (state, action) => {
        const tabState = ensureBoard(state, action.meta.arg.boardId)
        tabState.visibilityLoadingIds = tabState.visibilityLoadingIds.filter(
          (itemId) => itemId !== action.meta.arg.fieldSettingId,
        )
      })
      .addCase(setBoardStickerRequiredThunk.pending, (state, action) => {
        const tabState = ensureBoard(state, action.meta.arg.boardId)
        const fieldSettingId = action.meta.arg.fieldSettingId
        if (!tabState.requiredLoadingIds.includes(fieldSettingId)) {
          tabState.requiredLoadingIds.push(fieldSettingId)
        }
      })
      .addCase(setBoardStickerRequiredThunk.fulfilled, (state, action) => {
        const tabState = ensureBoard(state, action.payload.boardId)
        tabState.boardStickers = action.payload.stickers
        const fieldSettingId = action.meta.arg.fieldSettingId
        tabState.requiredLoadingIds = tabState.requiredLoadingIds.filter(
          (itemId) => itemId !== fieldSettingId,
        )
      })
      .addCase(setBoardStickerRequiredThunk.rejected, (state, action) => {
        const tabState = ensureBoard(state, action.meta.arg.boardId)
        tabState.requiredLoadingIds = tabState.requiredLoadingIds.filter(
          (itemId) => itemId !== action.meta.arg.fieldSettingId,
        )
      })
      .addCase(reorderBoardStickerThunk.pending, (state, action) => {
        const tabState = ensureBoard(state, action.meta.arg.boardId)
        tabState.reorderingStickerId = action.meta.arg.activeFieldSettingId
        tabState.boardStickers = action.meta.arg.reorderedBoardStickers
      })
      .addCase(reorderBoardStickerThunk.fulfilled, (state, action) => {
        const tabState = ensureBoard(state, action.payload.boardId)
        tabState.reorderingStickerId = null

        const apiStickers = action.payload.stickers
        const optimisticStickers = action.meta.arg.reorderedBoardStickers

        tabState.boardStickers = hasInvalidBoardStickerPositions(apiStickers)
          ? preferOptimisticBoardStickerPositions(
              apiStickers,
              optimisticStickers,
            )
          : apiStickers
      })
      .addCase(reorderBoardStickerThunk.rejected, (state, action) => {
        const payload = action.payload as {
          boardId: number
          previousBoardStickers: TaskTrackerBoardSticker[]
        }
        const tabState = ensureBoard(state, payload.boardId)
        tabState.reorderingStickerId = null
        tabState.boardStickers = payload.previousBoardStickers
      })
  },
})

export default boardStickersTabSlice.reducer
